import React from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../../../generic/systemValues/systemValueLabel'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { formatDuration } from '../duration'
import { buildStatusTimeline, timeByStatus } from '../statusTimeline'

// S422 — la LÍNEA DE TIEMPO DE ESTADOS de una operación. Sustituye a la franja de operarios; el
// porqué completo —y por qué los carriles por operario NO se simulan con `openedBy`— está en
// `statusTimeline.js`.

// Un tramo de pocos segundos sobre un eje de semanas mediría 0 px y desaparecería. Se le da un
// ancho mínimo para que exista, y por eso la barra NO se lee como duración: la duración está en el
// resumen y en la tabla, con su cifra.
const MIN_SEGMENT_PERCENT = 0.6

// ⚠️⚠️ EL EJE ACABA EN EL ÚLTIMO CAMBIO REGISTRADO, y lo que sigue abierto se dibuja ENTRANDO en una
// franja final que NO ESTÁ A ESCALA. El porqué, con las cifras, en `buildStatusTimeline`: medido
// hasta hoy, la historia quedaba en menos de un 25 % del eje en la mitad de las operaciones. La
// franja existe para que un abierto que empieza JUSTO en el último cambio —ancho cero a escala—
// se vea y lleve su rótulo, y para decir con la forma «esto sigue», sin medir cuánto.
const CONTINUES_PERCENT = 8
const SCALED_PERCENT = 100 - CONTINUES_PERCENT

// ⚠️ LOS COLORES SE ASIGNAN POR ORDEN DE APARICIÓN, NO POR CÓDIGO DE ESTADO, y es deliberado: el
// catálogo de estados es de la instalación, y un mapa `{IN_PROGRESS: verde, …}` escrito aquí dejaría
// sin color —o con uno inventado— al primer estado que alguien añada. Y como el color NUNCA es lo
// único que dice el estado (RN35), no hay nada que se pierda si dos instalaciones pintan distinto.
const PALETTE_KEYS = ['info', 'warning', 'success', 'error', 'secondary', 'primary']

// `timeline` is the result of `buildStatusTimeline`, when the caller already computed it (it also needs
// the hidden count): it is then not built a second time.
const StatusTimeline = ({ statusChanges = [], includeShort = false, timeline }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  const { axis, lanes, hiddenCount } = timeline || buildStatusTimeline(statusChanges, { includeShort })
  const { totals, openSegments } = timeByStatus(statusChanges)

  if (lanes.length === 0 && hiddenCount === 0) return null

  const colorKeyOf = new Map()
  statusChanges.forEach((change) => {
    const code = (change.status || {}).code || ''
    if (!colorKeyOf.has(code)) colorKeyOf.set(code, PALETTE_KEYS[colorKeyOf.size % PALETTE_KEYS.length])
  })

  const span = axis.startMs === null || axis.endMs === null ? 0 : axis.endMs - axis.startMs
  const positionOf = (segment) => {
    const left = span <= 0 ? 0 : ((segment.startMs - axis.startMs) / span) * SCALED_PERCENT
    // Un abierto llega hasta el borde a escala y ENTRA en la franja de «sigue»: acaba en el 100 %.
    if (segment.open) return { left, width: 100 - left }
    if (span <= 0) return { left: 0, width: SCALED_PERCENT }
    const width = Math.max(MIN_SEGMENT_PERCENT, ((segment.endMs - segment.startMs) / span) * SCALED_PERCENT)
    return { left, width: Math.min(width, SCALED_PERCENT - left) }
  }
  // Con varias máquinas cada una tiene SU estado actual, y el resumen dice de cuál es cada uno.
  const severalMachines = new Set(openSegments.map((segment) => String(segment.deviceId))).size > 1 || lanes.length > 1
  const at = (ms) => formatDateTime(new Date(ms).toISOString())
  const personOf = (worker) => (worker && (worker.name || worker.code)) || t_('Not reported')
  // Story 204 — S73: a status as the user reads it (RN2.1); the summary entries carry the same {code, name}.
  const statusLabelOf = (status) => systemValueLabel(t_, 'fabrication_order_status', status) || (status || {}).code || ''

  return (
    <Box data-testid='status-timeline'>
      {/* ⚠️⚠️ TIEMPO POR ESTADO — lo que más valor saca de la tabla nueva y hoy no existía en ninguna
          parte. Y el TRAMO ABIERTO VA APARTE, nunca sumado: es el ESTADO ACTUAL —no trabajo en
          marcha: puede ser «Finished»— y dura semanas. Se dice «estado actual desde…», uno por
          máquina, y NUNCA «en curso», que para un «Finished» sería falso. */}
      <Stack data-testid='status-time-summary' direction='row' spacing={1.5} flexWrap='wrap' useFlexGap sx={{ mb: 1 }}>
        {totals.map((total) => (
          <Typography key={total.code} data-testid={`status-total-${total.code}`} variant='body2'>
            <Box component='span' sx={{ fontWeight: 700 }}>{formatDuration(total.seconds)}</Box>
            {` ${statusLabelOf(total)}`}
          </Typography>
        ))}
        {openSegments.map((segment, index) => (
          <Typography key={`open-${index}`} data-testid='status-open-since' data-device={String(segment.deviceId)} variant='body2' sx={{ fontWeight: 700, color: 'info.main' }}>
            {severalMachines
              ? t_('Current status on {{machine}}: {{status}} since {{date}}', { machine: segment.deviceName || t_('Not reported'), status: statusLabelOf(segment), date: formatDateTime(segment.since) })
              : t_('Current status: {{status}} since {{date}}', { status: statusLabelOf(segment), date: formatDateTime(segment.since) })}
          </Typography>
        ))}
      </Stack>

      {/* El eje es ÚNICO y compartido: con un eje por carril, dos barras del mismo largo dirían
          duraciones distintas. Los extremos se rotulan una sola vez, y el final es el ÚLTIMO CAMBIO
          REGISTRADO: por eso se rotula encima del borde a escala, no al final de la franja de
          «sigue». */}
      <Stack direction='row' justifyContent='space-between' sx={{ mb: 0.5, width: `${SCALED_PERCENT}%` }}>
        <Typography data-testid='status-axis-start' variant='caption' sx={{ color: 'text.secondary' }}>
          {axis.startMs === null ? '' : at(axis.startMs)}
        </Typography>
        <Typography data-testid='status-axis-end' variant='caption' sx={{ color: 'text.secondary' }}>
          {axis.endMs === null ? '' : at(axis.endMs)}
        </Typography>
      </Stack>

      <Stack spacing={0.75}>
        {lanes.map((lane) => (
          <Stack key={String(lane.deviceId)} data-testid={`status-lane-${lane.deviceId}`} direction='row' spacing={2} alignItems='center'>
            {/* Con UNA máquina el carril no se rotula: no hay nada que distinguir. Con varias, cada
                carril dice de cuál es. */}
            {lanes.length > 1 && (
              <Typography variant='body2' sx={{ fontWeight: 700, minWidth: 120, flexShrink: 0 }}>
                {lane.deviceName || t_('Not reported')}
              </Typography>
            )}
            <Box sx={(theme) => ({ position: 'relative', flex: 1, height: 22, borderRadius: 1, backgroundColor: alpha(theme.palette.text.primary, 0.06) })}>
              {lane.segments.map((segment) => {
                const { left, width } = positionOf(segment)
                const colorKey = colorKeyOf.get(segment.status.code || '') || 'info'
                const statusName = statusLabelOf(segment.status) || t_('Not reported')
                const reasonLabel = systemValueLabel(t_, 'reason', segment.reason) || segment.reason.code || ''
                const title = [
                  statusName,
                  reasonLabel ? `${t_('Reason')}: ${reasonLabel}` : '',
                  `${t_('Opened by')}: ${personOf(segment.openedBy)}`,
                  segment.open ? t_('Current status') : `${t_('Closed by')}: ${personOf(segment.closedBy)}`,
                  segment.open ? `${at(segment.startMs)} → …` : `${at(segment.startMs)} → ${at(segment.endMs)}`
                ].filter(Boolean).join(' · ')
                return (
                  <Tooltip key={segment.key} title={title}>
                    {/* ⚠️⚠️ RN35 — EL ESTADO VA ROTULADO, NO SOLO COLOREADO. El nombre va dentro
                        del tramo —recortado si no cabe— y entero en el `aria-label`, que es lo que
                        lee quien no distingue el color. Un tramo solo coloreado diría «algo pasó»
                        sin decir qué. */}
                    <Box
                      data-testid={`status-segment-${segment.key}`}
                      // Focusable, so the tooltip opens on focus too and the keyboard reads the segment.
                      role='img'
                      tabIndex={0}
                      data-open={segment.open ? 'true' : 'false'}
                      data-continues={segment.open ? 'true' : 'false'}
                      data-status={segment.status.code || ''}
                      aria-label={title}
                      sx={(theme) => {
                        const color = (theme.palette[colorKey] || theme.palette.info).main
                        return {
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          left: `${left}%`,
                          width: `${width}%`,
                          display: 'flex',
                          alignItems: 'center',
                          overflow: 'hidden',
                          px: 0.5,
                          backgroundColor: alpha(color, 0.35),
                          borderLeft: `2px solid ${color}`,
                          // El tramo abierto se deja ABIERTO por la derecha, fundiéndose en la franja
                          // de «sigue»: no ha terminado, y un borde cerrado diría que sí.
                          ...(segment.open ? { background: `linear-gradient(90deg, ${alpha(color, 0.35)} ${100 - CONTINUES_PERCENT * 2}%, ${alpha(color, 0)} 100%)` } : {})
                        }
                      }}
                    >
                      <Typography component='span' variant='caption' sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {statusName}
                      </Typography>
                      {segment.open && (
                        <Box component='span' data-testid='status-segment-continues' aria-hidden sx={{ ml: 'auto', pl: 0.5, fontWeight: 700 }}>›</Box>
                      )}
                    </Box>
                  </Tooltip>
                )
              })}
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

export default StatusTimeline
